import React, { useEffect, useState, useRef } from 'react';
import { MsalProvider, useMsal } from '@azure/msal-react';
import * as portalSession from './auth/portalSession';
import { PublicClientApplication } from '@azure/msal-browser';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import { msalConfig } from './auth/msalConfig';
import Login from './components/Auth/Login';
import NotInvited from './components/Auth/NotInvited';
import Dashboard from './components/Dashboard/Dashboard';
import ProjectDirectory from './components/Dashboard/ProjectDirectory';
import Home from './components/Home'; // Import the new Home component
import AdminPanel from './components/Admin/AdminPanel';
import Header from './components/Layout/Header';
import LoadingSpinner from './components/Layout/LoadingSpinner';
import AppLayout from './components/Layout/AppLayout';
import CreateProjectModal from './components/Admin/CreateProjectModal';
import { authenticatedApiCall } from './utils/apiClient';
import tokenManager from './utils/tokenManager';
import { usePermissions } from './hooks/usePermissions';
import Tutorial from './components/Tutorial/Tutorial';
import { HelpModal, LearningCenterModal, ContactModal, FAQModal } from './components/Help';
import HelpbotWidget from './components/Helpbot/HelpbotWidget';
import { AudioGuideProvider } from './components/common/AudioGuide';

// ProjectPage with DisciplineMatrix - optimized matrix component
import ProjectPage from './components/Project/ProjectPage';

// Standalone Contractors page (not project-specific)
import ContractorsPage from './components/Procurement/ContractorsPage';
// Resource Management (Resources department)
import ResourceManagementPage from './components/Resources/ResourceManagementPage';
import FabricationHistogramPage from './components/Resources/FabricationHistogramPage';
import PortfolioEvmPage from './components/Resources/PortfolioEvmPage';

// Standalone Actual Spent Hours page (project selector required)
import ActualSpentPage from './components/Actuals/ActualSpentPage';

// Standalone Coding Structure page (accounting)
import CodingStructurePage from './components/Accounting/CodingStructurePage';
import HiringLayoffPage from './components/Accounting/HiringLayoffPage';

// Standalone Estimation page (no project required)
import EstimationPage from './components/Estimation/EstimationPage';
import MtoSpecsPage from './components/Estimation/MtoSpecsPage';
import { resolveMenuOnEstimateSelect } from './utils/estimationNavMemory';

// Material Management dashboard (Procurement master view; Estimation/PM scoped)
import MaterialManagementDashboard from './components/Procurement/MaterialManagementDashboard';

// Standalone pages for other menus that don't require a project
import { HSEPage, TrainingPage, ProcessesPage, DevelopmentPage, DownloadAppPage, SalesPage } from './components/common/StandalonePage';
import SubcontractorDiscovery from './components/PMO/SubcontractorDiscovery';

// Quality department page
import QualityPage from './components/Quality/QualityPage';
import MobileProgressApp from './mobile/progress/MobileProgressApp';
import MobileAdminScreen from './mobile/progress/MobileAdminScreen';
import InstallAppPrompt from './mobile/progress/InstallAppPrompt';
import { NativeWebsiteBar } from './mobile/progress/NativeWebsiteBar';
import { peekFieldRoute } from './mobile/progress/fieldRouteMemory';
import { shouldBounceNativeToField, shouldRedirectPhoneToField, shouldShowNativeWebsiteBar } from './mobile/progress/nativeShell';

/**
 * ProjectPageWrapper - Forces remount when projectId changes
 * This ensures proper Socket.IO cleanup when navigating between projects
 */
const ProjectPageWrapper = () => {
  const { projectId } = useParams();
  // Key forces React to fully unmount and remount ProjectPage when projectId changes
  return <ProjectPage key={projectId} />;
};

// Create MSAL instance once at module level (singleton)
// MSAL v3+ requires initialize() to be called before using the instance
let msalInstance = null;
let msalInitializePromise = null;

const getMsalInstance = () => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    // Start initialization immediately
    msalInitializePromise = msalInstance.initialize().then(() => {
      // Handle redirect promise after initialization
      msalInstance.handleRedirectPromise().catch(error => {
        console.error('Error handling redirect:', error);
      });
      return msalInstance;
    });
  }
  return msalInstance;
};

// Export the initialization promise so other modules can wait for MSAL to be ready
export const waitForMsalInit = () => {
  // Ensure instance is created (which starts initialization)
  getMsalInstance();
  return msalInitializePromise;
};

const MainContent = () => {
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const showAdminPanel = location.pathname === '/admin';
  const setShowAdminPanel = (open) => {
    if (open) navigate('/admin');
    else if (location.pathname === '/admin') navigate('/');
  };
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [showLearningModal, setShowLearningModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('contact') === 'true';
  });
  const [selectedDepartment, setSelectedDepartment] = useState(() => {
    // Load saved department from localStorage or default to 'projects'
    try {
      return localStorage.getItem('selectedDepartment') || 'projects';
    } catch {
      return 'projects';
    }
  });
  // Full estimate object so the header shows estimate_number (like project name).
  const [selectedEstimate, setSelectedEstimate] = useState(() => {
    try {
      const s = localStorage.getItem('estimation_selected_estimate');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  const selectedEstimateId = selectedEstimate?.id || null;
  
  // Save selected department to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('selectedDepartment', selectedDepartment);
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [selectedDepartment]);

  // Persist selected estimate (id + number + name); clear LS when none selected
  useEffect(() => {
    try {
      if (selectedEstimate) {
        localStorage.setItem('estimation_selected_estimate', JSON.stringify(selectedEstimate));
      } else {
        localStorage.removeItem('estimation_selected_estimate');
      }
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [selectedEstimate]);

  // Project context and estimate context are mutually exclusive in the header
  useEffect(() => {
    if (/^\/project\/\d+/.test(location.pathname) && selectedEstimate) {
      setSelectedEstimate(null);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-links (e.g. email → /hse?menu=safety-training-db) should land in the
  // matching department so the sidebar shows the right place, not Home/Projects.
  useEffect(() => {
    const pathDept = {
      '/hse': 'hse',
      '/estimation': 'estimation',
      '/sales': 'sales',
      '/training': 'pmo',
      '/processes': 'pmo',
      '/development': 'pmo',
      '/resource-management': 'resources',
      '/fabrication-histogram': 'resources',
      '/portfolio-evm': 'resources',
    }[location.pathname];
    if (pathDept && selectedDepartment !== pathDept) {
      setSelectedDepartment(pathDept);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Handler for department change - reset project (via navigate home) and estimate
  const handleDepartmentChange = (newDepartment) => {
    if (newDepartment !== selectedDepartment) {
      setSelectedDepartment(newDepartment);
      setSelectedEstimate(null);
      // Navigate to home page so user can select a menu item from the new department
      navigate('/');
    }
  };

  const handleSelectEstimate = (est) => {
    setSelectedEstimate(est || null);
    if (est) {
      setSelectedDepartment('estimation');
      const menuItem = resolveMenuOnEstimateSelect({
        estimateId: est.id,
        alreadyOnEstimation: location.pathname === '/estimation',
      });
      navigate('/estimation', {
        state: menuItem ? { menuItem, estimate: est } : { estimate: est },
      });
    }
  };

  // Selecting a project leaves estimation context — header should show "Select Estimate"
  const handleSelectProject = () => {
    setSelectedEstimate(null);
  };
  
  // Ref to prevent duplicate requests in StrictMode
  const isInitializing = useRef(false);
  const hasInitialized = useRef(false);

  // Use centralized permission hook - replaces scattered role checks
  const permissions = usePermissions(user);

  // Listen for admin panel open/close events from sidebar
  useEffect(() => {
    const handleOpenAdminPanel = () => {
      if (permissions.canAccessAdminPanel) navigate('/admin');
    };
    const handleCloseAdminPanel = () => {
      if (location.pathname === '/admin') navigate('/');
    };
    window.addEventListener('openAdminPanel', handleOpenAdminPanel);
    window.addEventListener('closeAdminPanel', handleCloseAdminPanel);
    return () => {
      window.removeEventListener('openAdminPanel', handleOpenAdminPanel);
      window.removeEventListener('closeAdminPanel', handleCloseAdminPanel);
    };
  }, [permissions.canAccessAdminPanel, location.pathname, navigate]);

  const retryTimeoutRef = useRef(null);
  // 403 NOT_INVITED from /auth/me: signed in to Microsoft, not on the portal guest list.
  const [notInvited, setNotInvited] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const getUser = async (retryCount = 0) => {
      if (cancelled) return;

      if (!accounts[0] && !portalSession.isActive()) {
        setLoading(false);
        return;
      }

      if (isInitializing.current) return;
      if (hasInitialized.current) {
        setLoading(false);
        return;
      }

      isInitializing.current = true;

      try {
        const userResponse = await authenticatedApiCall(
          instance, 
          accounts, 
          'GET', 
          '/auth/me'
        );

        if (cancelled) return;

        const userData = userResponse.data.user;
        if (!userData.display_name && accounts[0]?.name) {
          userData.display_name = accounts[0].name;
        }
        setUser(userData);
        hasInitialized.current = true;

        const tutorialStarted = localStorage.getItem('lgl_tutorial_started');
        const tutorialCompleted = localStorage.getItem('lgl_tutorial_completed');
        const isNewUser = !tutorialStarted && (!tutorialCompleted || JSON.parse(tutorialCompleted).length === 0);
        if (isNewUser) {
          setTimeout(() => {
            if (!cancelled) setShowTutorial(true);
          }, 1000);
        }

        const dashboardResponse = await authenticatedApiCall(
          instance, 
          accounts, 
          'GET', 
          '/dashboard'
        );

        if (!cancelled) {
          setDashboardData(dashboardResponse.data);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error getting user data:', error);
        hasInitialized.current = false;

        if (error?.response?.status === 403 && error?.response?.data?.code === 'NOT_INVITED') {
          setNotInvited(true);
          return; // no retries — the answer will not change until an admin adds the e-mail
        }

        const MAX_RETRIES = 5;
        if (retryCount < MAX_RETRIES) {
          const delay = Math.min(2000 * Math.pow(2, retryCount), 30000);
          console.log(`Retrying user data load in ${delay / 1000}s (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
          retryTimeoutRef.current = setTimeout(() => getUser(retryCount + 1), delay);
        } else {
          console.error('Failed to load user data after all retries. Refresh the page to try again.');
        }
      } finally {
        isInitializing.current = false;
        if (!cancelled) setLoading(false);
      }
    };

    getUser();

    return () => {
      cancelled = true;
      isInitializing.current = false;
      hasInitialized.current = false;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [instance, accounts]);

  const handleLogout = () => {
    // Fully reset token manager state for intentional logout
    tokenManager.fullReset();
    if (portalSession.isActive()) {
      // E-mail-code session: revoke on the server, drop the token, back to the sign-in page.
      portalSession.logout().finally(() => { window.location.href = window.location.origin; });
      return;
    }
    // Use redirect flow - avoids popup blockers and COOP issues
    instance.logoutRedirect();
  };

  // Use MSAL account (or the portal session) as fallback while /auth/me is loading
  const sessionUser = portalSession.current()?.user;
  const effectiveUser = user || (accounts[0] ? {
    display_name: accounts[0].name || '',
    email: accounts[0].username || '',
  } : (sessionUser ? { display_name: sessionUser.display_name || '', email: sessionUser.email || '' } : null));

  if (loading) {
    return <LoadingSpinner />;
  }

  if (notInvited) {
    return <NotInvited email={accounts[0]?.username || sessionUser?.email} onSignOut={handleLogout} />;
  }

  const handleProjectCreated = (newProject) => {
    // Refresh dashboard data to show new project
    setShowCreateProjectModal(false);
    // Trigger a refresh by updating dashboardData
    window.location.reload();
  };

  const fieldApp = <MobileProgressApp user={effectiveUser} />;
  const fieldAppPreview = <MobileProgressApp demo user={effectiveUser} />;
  const isFieldApp = location.pathname === '/m' || location.pathname.startsWith('/m/');
  // Field APK WebView: after Azure AD, MSAL lands on origin (/) — bounce to /m.
  // Browser phones also open /m. Tablets and desktops stay on the regular portal.
  // NDE / Testing / Open full website / ?desktop=1 set a session flag so they stay here.
  if (shouldBounceNativeToField(location.pathname) || shouldRedirectPhoneToField(location.pathname)) {
    return <Navigate to={peekFieldRoute() || '/m'} replace />;
  }

  if (isFieldApp) {
    return (
      <Routes>
        <Route path="/m/preview" element={fieldAppPreview} />
        <Route path="/m/preview/admin" element={
          <MobileAdminScreen user={effectiveUser || { primary_role_id: 'superadmin', effective_authority_level: 'System', display_name: 'Preview Admin' }} />
        } />
        <Route path="/m/preview/settings" element={fieldAppPreview} />
        <Route path="/m/preview/preferences" element={fieldAppPreview} />
        <Route path="/m/preview/time" element={fieldAppPreview} />
        <Route path="/m/preview/coding" element={fieldAppPreview} />
        <Route path="/m/preview/jobs" element={fieldAppPreview} />
        <Route path="/m/preview/dept/:deptId" element={fieldAppPreview} />
        <Route path="/m/preview/estimate/:estimateId" element={fieldAppPreview} />
        <Route path="/m/preview/job/:projectId" element={fieldAppPreview} />
        <Route path="/m/preview/job/:projectId/:disciplineId" element={fieldAppPreview} />
        <Route path="/m/preview/job/:projectId/:disciplineId/:rowId" element={fieldAppPreview} />
        <Route path="/m/admin" element={
          permissions.canAccessAdminPanel
            ? <MobileAdminScreen user={effectiveUser} />
            : <Navigate to="/m" replace />
        } />
        <Route path="/m" element={fieldApp} />
        <Route path="/m/settings" element={fieldApp} />
        <Route path="/m/preferences" element={fieldApp} />
        <Route path="/m/time" element={fieldApp} />
        <Route path="/m/coding" element={fieldApp} />
        <Route path="/m/jobs" element={fieldApp} />
        <Route path="/m/dept/:deptId" element={fieldApp} />
        <Route path="/m/estimate/:estimateId" element={fieldApp} />
        <Route path="/m/job/:projectId" element={fieldApp} />
        <Route path="/m/job/:projectId/:disciplineId" element={fieldApp} />
        <Route path="/m/job/:projectId/:disciplineId/:rowId" element={fieldApp} />
      </Routes>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {shouldShowNativeWebsiteBar(location.pathname) ? <NativeWebsiteBar /> : null}
      <Header 
        user={effectiveUser}
        permissions={permissions}
        showAdminPanel={showAdminPanel}
        setShowAdminPanel={setShowAdminPanel}
        handleLogout={handleLogout}
        onCreateProject={() => setShowCreateProjectModal(true)}
        onHelpClick={() => setShowHelpModal(true)}
        onFAQClick={() => setShowFAQModal(true)}
        onLearningClick={() => setShowLearningModal(true)}
        onContactClick={() => setShowContactModal(true)}
        selectedDepartment={selectedDepartment}
        onSelectDepartment={handleDepartmentChange}
        selectedEstimate={selectedEstimate}
        selectedEstimateId={selectedEstimateId}
        onSelectEstimate={handleSelectEstimate}
        onSelectProject={handleSelectProject}
      />

      {/* Main Content */}
      <Routes>
        {/* Default Route is now Home with Sidebar */}
        <Route path="/" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <Home />
          </AppLayout>
        } />

        {/* Admin Panel — real URL so refresh stays here */}
        <Route path="/admin" element={
          permissions.canAccessAdminPanel ? (
            <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
              <AdminPanel onClose={() => navigate('/')} user={user} />
            </AppLayout>
          ) : <Navigate to="/" replace />
        } />
        
        {/* My Projects with Sidebar */}
        <Route path="/projects" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <Dashboard data={dashboardData} user={user} />
          </AppLayout>
        } />
        
        {/* Project Directory - standalone route, shows all projects with 'Projects' header */}
        <Route path="/project-directory" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <ProjectDirectory />
          </AppLayout>
        } />
        
        {/* Contractors Database - standalone route, not project-specific.
            Suppliers are the same screen, toggled via the in-page Subcontractor/Supplier dropdown. */}
        <Route path="/contractors" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <ContractorsPage />
          </AppLayout>
        } />
        
        {/* Resource Management */}
        <Route path="/resource-management" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <ResourceManagementPage />
          </AppLayout>
        } />
        <Route path="/fabrication-histogram" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <FabricationHistogramPage />
          </AppLayout>
        } />
        <Route path="/portfolio-evm" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <PortfolioEvmPage />
          </AppLayout>
        } />
        {/* Actual Spent Hours - standalone route with project selector */}
        <Route path="/actual-spent" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <ActualSpentPage />
          </AppLayout>
        } />
        
        {/* Coding Structure - standalone route for accounting */}
        <Route path="/coding-structure" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <CodingStructurePage />
          </AppLayout>
        } />

        {/* Hiring / Layoff — master employee list (Accounting + superadmin) */}
        <Route path="/hiring-layoff" element={
          permissions.canAccessHiringDatabase ? (
            <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
              <HiringLayoffPage />
            </AppLayout>
          ) : <Navigate to="/" replace />
        } />
        
        {/* Estimation - standalone route for estimation tools (requires Estimating or Construction dept) */}
        <Route path="/estimation" element={
          permissions.hasDepartment(3) || permissions.hasDepartment(1) ? (
            <AppLayout
              projectId={null}
              selectedDepartment={selectedDepartment}
              onSelectDepartment={handleDepartmentChange}
              selectedEstimateId={selectedEstimateId}
              selectedEstimateNumber={selectedEstimate?.estimate_number || null}
            >
              <EstimationPage onEstimateChange={setSelectedEstimate} />
            </AppLayout>
          ) : <Navigate to="/" replace />
        } />
        
        {/* MTO Specs download page - linked from the Send-to-Purchasing email */}
        <Route path="/mto-specs" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <MtoSpecsPage />
          </AppLayout>
        } />

        {/* Material Management dashboard - master under Procurement; scoped by ?estimate= / ?job= */}
        <Route path="/material-management" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <MaterialManagementDashboard />
          </AppLayout>
        } />

        {/* HSE - standalone route */}
        <Route path="/hse" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <HSEPage />
          </AppLayout>
        } />
        
        {/* Training - standalone route */}
        <Route path="/training" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <TrainingPage />
          </AppLayout>
        } />
        
        {/* Processes - standalone route */}
        <Route path="/processes" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <ProcessesPage />
          </AppLayout>
        } />
        
        {/* Development - standalone route */}
        <Route path="/development" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <DevelopmentPage />
          </AppLayout>
        } />
        
        {/* Subcontractor Discovery - standalone route (PMO) */}
        <Route path="/subcontractor-discovery" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <SubcontractorDiscovery />
          </AppLayout>
        } />

        {/* Sales - standalone route */}
        <Route path="/sales" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <SalesPage />
          </AppLayout>
        } />
        
        {/* Download App - standalone route */}
        <Route path="/download-app" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <DownloadAppPage />
          </AppLayout>
        } />
        
        {/* Quality - standalone route */}
        <Route path="/quality" element={
          <AppLayout projectId={null} selectedDepartment={selectedDepartment} onSelectDepartment={handleDepartmentChange}>
            <QualityPage />
          </AppLayout>
        } />
        
        {/* Project route - uses optimized ProjectPage with DisciplineMatrix */}
        {/* ProjectPageWrapper forces remount when projectId changes for proper socket cleanup */}
        <Route path="/project/:projectId" element={<ProjectPageWrapper />} />
      </Routes>

      {/* Tutorial Overlay */}
      <Tutorial
        user={user}
        isActive={showTutorial}
        onClose={() => setShowTutorial(false)}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        onStartTutorial={() => {
          setShowHelpModal(false);
          setShowTutorial(true);
        }}
        user={user}
      />

      {/* FAQ Modal */}
      <FAQModal
        isOpen={showFAQModal}
        onClose={() => setShowFAQModal(false)}
      />

      {/* Learning Center Modal */}
      <LearningCenterModal
        isOpen={showLearningModal}
        onClose={() => setShowLearningModal(false)}
      />

      {/* Contact Us Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      {/* Create Project Modal for PM users */}
      <CreateProjectModal
        isOpen={showCreateProjectModal}
        onClose={() => setShowCreateProjectModal(false)}
        onProjectCreated={handleProjectCreated}
      />

      {/* Global Help Chatbot - visible on all pages */}
      <HelpbotWidget />
    </div>
  );
};

const UnauthenticatedRoutes = () => {
  const location = useLocation();
  if (shouldRedirectPhoneToField(location.pathname)) {
    return <Navigate to="/m" replace />;
  }
  return (
    <Routes>
      <Route path="/m/preview" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/settings" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/preferences" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/time" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/coding" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/jobs" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/dept/:deptId" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/estimate/:estimateId" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/job/:projectId" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/job/:projectId/:disciplineId" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/job/:projectId/:disciplineId/:rowId" element={<MobileProgressApp demo />} />
      <Route path="/m/preview/admin" element={
        <MobileAdminScreen user={{ primary_role_id: 'superadmin', effective_authority_level: 'System', display_name: 'Preview Admin' }} />
      } />
      <Route path="*" element={<Login />} />
    </Routes>
  );
};

/**
 * Signed in = an MSAL account (Microsoft) OR a portal session (external user
 * who signed in with an e-mail code). MSAL's own templates only know the first.
 */
function AuthGate() {
  const { accounts } = useMsal();
  const [portalActive, setPortalActive] = useState(portalSession.isActive());
  useEffect(() => {
    const onChange = () => setPortalActive(portalSession.isActive());
    window.addEventListener('lgl-portal-session', onChange);
    window.addEventListener('storage', onChange);
    return () => { window.removeEventListener('lgl-portal-session', onChange); window.removeEventListener('storage', onChange); };
  }, []);
  if (accounts.length > 0 || portalActive) return <MainContent />;
  return <UnauthenticatedRoutes />;
}

function App() {
  // Use singleton MSAL instance to avoid duplicate instance warnings
  const pca = getMsalInstance();
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    // Wait for MSAL to initialize before rendering
    if (msalInitializePromise) {
      msalInitializePromise.then(() => {
        setIsInitialized(true);
      }).catch(error => {
        console.error('MSAL initialization error:', error);
        setIsInitialized(true); // Still render to show error state
      });
    } else {
      setIsInitialized(true);
    }
  }, []);
  
  // Show loading while MSAL initializes
  if (!isInitialized) {
    return <LoadingSpinner />;
  }
  
  return (
    <MsalProvider instance={pca}>
      {/* AudioGuideProvider: portal-wide help narration (text-to-speech).
          Mounted at the root so the floating mini-player keeps playing
          across page navigation and after help modals close. */}
      <AudioGuideProvider>
        <Router>
          <InstallAppPrompt />
          <AuthGate />
        </Router>
      </AudioGuideProvider>
    </MsalProvider>
  );
}

export default App;
